<template>
    <div class="card position-relative">
        <div v-if="loadingSandboxes || loadingContent" class="overlay-loader d-flex justify-content-center align-items-center">
            <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">{{ loadingSandboxes ? 'Loading sandboxes...' : 'Loading content...' }}</span>
            </div>
        </div>
        <form @submit.prevent="onDeployJobStack">
            <div class="card-body">
                <div v-if="error" class="text-danger mb-2">
                    {{ error }}
                </div>
                <div class=" d-flex flex-row ">
                    <FormFields :fields="fields" v-model="deployStack" colField="col-3 me-5" />
                </div>
                <div v-if="Object.keys(parameterList).length > 0" class="mb-4">
                    <h4>Parameters</h4>
                    <FormFields :fields="parameters" v-model="parameterList" :inlineField="true" />
                </div>
                <div v-if="templateJobOptions.length > 0">
                    <h4>Overrides</h4>
                    <Tabs :tabs="tabItems">
                        <template v-for="templatejob in templateJobOptions" :key="templatejob.value"
                                v-slot:[templatejob.value]>
                            <div :class="['card', !deployStack.sandbox ? 'disabled' : '']">
                                <div class="card-body">
                                    <input-field label="Destination Name" type="text" v-model="currentActiveJob.dst_name" />
                                    <span v-if="disableOverride" class="text-warning">Note: Sandbox is a DEV, so metering and
                                        schedule won't be applied to the jobs.</span>
                                    <input-field :editable="!disableOverride" label="Schedule" type="cron"
                                        v-model="currentActiveJob.schedule" />
                                    <input-field :editable="!disableOverride" label="Enable Schedule" type="checkbox"
                                        v-model="currentActiveJob.enable_schedule" />

                                    <input-field :editable="!disableOverride" label="Detach Meters" type="checkbox"
                                        v-model="currentActiveJob.detach_meters" />

                                    <div class="table-responsive">
                                        <span>Meters</span>
                                        <table class="table table-sm table-nowrap mt-2">
                                            <thead>
                                                <tr class="">
                                                    <th class="col-sm-5">Label</th>
                                                    <th class="col-sm-5">Metrics Data Channel</th>
                                                    <th class="col-sm-5">Metrics Type</th>
                                                    <th class="col-sm-5">Metrics Field</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr v-for="(meter, index) in currentActiveJob.meters" :key="index">
                                                    <td class="col-sm-5 p-2">
                                                        <input list="meterLabelOptions" style="padding: 1px 7px;"
                                                            class="form-control"
                                                            v-model="currentActiveJob.meters[index].label" />
                                                        <datalist id="meterLabelOptions">
                                                            <option v-for="option in meterLabels" :key="option" :value="option">
                                                                {{ option }}
                                                            </option>
                                                        </datalist>
                                                    </td>
                                                    <td class="col-sm-5 p-2">
                                                        <input type="text" style="padding: 1px 7px;" class="form-control"
                                                            v-model="currentActiveJob.meters[index].metrics_data_channel" />
                                                    </td>
                                                    <td class="col-sm-5 p-2">
                                                        <input style="padding: 1px 7px;" class="form-control" type="text"
                                                            v-model="currentActiveJob.meters[index].metrics_type" />
                                                    </td>
                                                    <td class="col-sm-5 p-2">
                                                        <input style="padding: 1px 7px;" class="form-control" type="text"
                                                            v-model="currentActiveJob.meters[index].metrics_field" />
                                                    </td>
                                                    <td class="col-sm-1">
                                                        <i @click="removeMeter(index)" class="fa fa-regular fa-trash-can"></i>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <div class="mb-4">
                                        <button type="button" class="btn btn-sm btn-primary float-end m-3" @click="onAddMeter"
                                            :disabled="currentActiveJob.detach_meters || disableOverride">
                                            Add Meter
                                        </button>
                                    </div>
                                    <input-field label="Vars" type="yaml" v-model="currentActiveJob.vars" />

                                </div>
                            </div>
                        </template>
                    </Tabs>
                </div>
            </div>
            <div class="card-footer">
                <div class="text-end">
                    <SubmitButton :isDisabled="!this.isParametersValid" :fields="fields" v-model="deployStack"
                        :loading="loading" />
                </div>
            </div>
        </form>
    </div>
</template>
<script>
import FormFields from "../../components/FormFields.vue";
import SubmitButton from "../../components/SubmitButton.vue";
import { mapState, mapActions } from "vuex";
import Fields from "../../utils/field-constants/deployJobStackFields";
import InputField from "../../components/InputField.vue";
import Tabs from "../../components/Tabs.vue";
export default {
    components: { FormFields, SubmitButton, InputField, Tabs },
    data() {
        return {
            error: "",
            loading: false,
            fields: Fields,
            deployStack: {
                cid: this.$store.state.currentClient.cid
            },
            jobStack: {},
            parameterList: {},
            parameters: [],
            templateJobs: {},
            meterLabels: [],
            loadingSandboxes: false,
            loadingContent: false,
            disableOverride: false,
            tabItems: []
        };
    },
    name: "DeployJobStack",
    computed: {
        ...mapState({
            sandboxes: (state) => state.sandbox.sandboxes,
            clients: (state) => state.clients,
        }),
        cidOptions() {
            return this.clients.map((cl) => {
                return {
                    label: cl.cid,
                    value: cl.cid,
                };
            });
        },
        templateJobOptions() {
            return this.jobStack.jobs?.map((job) => { return { label: job.name, value: job.name } }) || []
        },
        isParametersValid() {
            return Object.values(this.parameterList).every(value => 
                value !== '' && value !== null && value !== undefined
            );
        },
        currentActiveJob() {
            const activeTab = this.$store.state.activeTab;
            if (activeTab && this.templateJobOptions.find(job => job.value === activeTab)) {
                return this.templateJobs[activeTab] || {};
            }
            return {};
        },
        firstTemplateJobDestName() {
            const firstTemplateJob = Object.values(this.templateJobs)[0];
            return firstTemplateJob?.dst_name || this.templateJobOptions[0]?.value || this.jobStack.name;
        }
    },
    watch: {
        'jobStack.jobs': {
            handler(newVal) {
                if (newVal) {
                    this.loadingContent = true;
                    
                    const allParams = newVal.flatMap(job => job.parameters?.map(param => param.name) || []);
                    const uniqueParams = [...new Set(allParams)];

                    this.parameters = uniqueParams.map(paramName => ({
                        label: paramName, 
                        name: paramName, 
                        placeholder: "Enter value for " + paramName,
                        validation: [
                            { type: "required", errMsg: paramName + " is required" }
                        ]
                    }));

                    this.parameterList = {};
                    uniqueParams.forEach(paramName => {
                        this.parameterList[paramName] = "";
                    });

                    this.tabItems = newVal.map((job, index)=>{
                        return {
                            name: job.name,
                            id: "id"+index,
                            slot: job.name
                        }
                    })
                    if (this.tabItems.length > 0 && !this.$store.state.activeTab) {
                        this.$store.dispatch('setActiveTab', this.tabItems[0].name);
                    }
                    this.ensureActiveTabTemplateJob();
                    this.$nextTick(() => {
                        setTimeout(() => {
                            this.loadingContent = false;
                        }, 100);
                        
                    });
                } else if (this.loadingContent) {
                    this.loadingContent = false;
                }
            },
            immediate: true
        },
        templateJobs: {
            handler() {
                const activeTab = this.$store.state.activeTab;
                if (activeTab && this.templateJobs[activeTab]?.detach_meters) {
                    delete this.templateJobs[activeTab].meters;
                }
            },
            deep: true
        },
        '$store.state.activeTab'(newVal) {
            if (newVal) {
                this.ensureActiveTabTemplateJob();
            }
        },
        'deployStack.cid': {
            handler(newVal) {
                this.error = null;
                if (!newVal) return;
                this.loadingSandboxes = true;
                delete this.deployStack.sandbox
                this.fetchSandboxes(newVal)
                    .finally(() => this.loadingSandboxes = false);
            },
            immediate: true,
        },
        'deployStack.sandbox'(newVal) {
            if (newVal && newVal.env == 'DEV') {
                this.onClearOverride();
                this.disableOverride = true;
            } else {
                this.disableOverride = false;
                this.ensureActiveTabTemplateJob();
            }
        },
        sandboxes(newVal) {
            if (newVal && newVal.length) {
                const sandboxOptions = newVal.map(sandbox => ({
                    label: sandbox.name,
                    value: { name: sandbox.name, env: sandbox.env }
                }));
                const sandboxField = this.fields.find(f => f.name === 'sandbox');
                if (sandboxField) sandboxField.options = sandboxOptions;
            } else {
                const sandboxField = this.fields.find(f => f.name === 'sandbox');
                if (sandboxField) sandboxField.options = [];
                this.error = "No Sandboxes available!"
                this.onClearOverride();
            }
        },
    },
    mounted() {
        this.initializeOptions();
        if (this.$route.params?.data) {
            this.loadingContent = true;
            this.jobStack = JSON.parse(this.$route.params.data);
        } else {
            this.fetchJobStack();
        }
        this.getMeterLabels();
    },
    methods: {
        ...mapActions({
            readJobStack: "amdp/readJobStack",
            deployJobStack: "amdp/deployJobStack",
            fetchMeterLabels: "amdp/fetchMeterLabels",
            fetchSandboxes: "sandbox/fetchSandboxes"
        }),
        initializeOptions() {
            const cidField = this.fields.find(f => f.name === 'cid');
            if (cidField) cidField.options = this.cidOptions;
        },
        async onDeployJobStack() {
            this.loading = true;
            try {
                const parametersArray = Object.keys(this.parameterList).map(key => ({
                    name: key,
                    value: this.parameterList[key]
                }));

                const payload = {
                    cid: this.deployStack.cid,
                    sandbox: this.deployStack.sandbox.name,
                    name: this.deployStack.name,
                    parameters: parametersArray,
                    ...(Object.keys(this.templateJobs).length > 0 && {
                        overrides: Object.values(this.templateJobs)
                    })
                };

                await this.deployJobStack({ name: this.jobStack.name, data: payload });
                
                this.$store.dispatch("addToast", {
                    message: "Job Stack deployed successfully.",
                    type: "success",
                });
                this.$store.dispatch('setCurrentClient', this.deployStack.cid);
                if(this.deployStack.sandbox.env === 'DEV')
                    this.$router.push({
                        name: "Sandbox Job Configuration",
                        params: {
                            cid: this.deployStack.cid,
                            sandboxId: this.deployStack.sandbox.name,
                            jobId: this.firstTemplateJobDestName
                        }
                    });
                else
                    this.$router.push({
                        name: "Job Configuration",
                        params: {
                            cid: this.deployStack.cid,
                            sandboxId: this.deployStack.sandbox.name,
                            jobId: this.firstTemplateJobDestName
                        }
                    });
                await this.$store.dispatch('sandbox/fetchSandboxes', this.deployStack.cid);
            } catch (error) {
                this.error = error.response?.data?.error || error.message;
                this.$store.dispatch("addToast", {
                    message: this.error || "Something went wrong when creating Job Stack. Please contact support.",
                    type: "error",
                });
            } finally {
                this.loading = false;
            }
        },
        async fetchJobStack() {
            this.loadingContent = true;
            try {
                const response = await this.readJobStack(this.$route.params.name);
                this.jobStack = response.data;
            } catch (error) {
                this.loadingContent = false;
                this.error = error.response?.data?.error || error.message;
            }
        },
        onAddMeter() {
            const activeTab = this.$store.state.activeTab;
            if (activeTab && this.templateJobs[activeTab]) {
                if (!this.templateJobs[activeTab].meters) {
                    this.templateJobs[activeTab].meters = [];
                }
                this.templateJobs[activeTab].meters.push({
                    label: null,
                    metrics_type: null,
                    metrics_field: null,
                    metrics_data_channel: null,
                });
            }
        },
        removeMeter(index) {
            const activeTab = this.$store.state.activeTab;
            if (activeTab && this.templateJobs[activeTab]?.meters) {
                this.templateJobs[activeTab].meters.splice(index, 1);
            }
        },
        async getMeterLabels() {
            try {
                const response = await this.fetchMeterLabels();
                this.meterLabels = response?.data?.meter_labels || [];
            } catch (error) {
                console.warn('Failed to fetch meter labels:', error);
                this.meterLabels = [];
            }
        },
        ensureActiveTabTemplateJob() {
            const activeTab = this.$store.state.activeTab;
            if (activeTab && this.templateJobOptions.find(job => job.value === activeTab)) {
                if (!this.templateJobs[activeTab]) {
                    this.templateJobs[activeTab] = { name: activeTab, dst_name: activeTab };
                } else if (!this.templateJobs[activeTab].dst_name) {
                    this.templateJobs[activeTab].dst_name = activeTab;
                }
            }
        },
        onClearOverride() {
            this.templateJobs = {};
            this.ensureActiveTabTemplateJob();
        }
    },
};
</script>

<style scoped>
.disabled {
    pointer-events: none;
    opacity: 0.5;
}

.overlay-loader {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(255, 255, 255, 0.8);
    z-index: 10;
    min-height: 400px;
}
</style>